import React from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Divider, Flex, Icon, LinkButton, Tag, Text } from 'core/components';
import { FiMaximize2 } from 'react-icons/fi';
import { CLOUD_PROVIDERS } from 'app/util/constants';

interface AgentPopoverCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  agent: any;
  /** When provided, renders an "Open details" action that opens the agent side panel. */
  onOpenDetails?: () => void;
  /** Fallback when the side panel isn't available: renders an "Open Full Page" link. */
  fullPageHref?: string;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '' || value === '---') {
    return null;
  }
  return (
    <Box mb="6px">
      <Text as="div" small fontWeight="bold">
        {label}
      </Text>
      <Text as="div" small muted style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Box>
  );
}

/** Compact agent summary shown in the AgentDisplay hover popover (site / labels / ASN / IPs). */
const AgentPopoverCard = observer(({ agent, onOpenDetails, fullPageHref }: AgentPopoverCardProps) => {
  if (!agent) {
    return null;
  }

  const AgentIcon = agent.icon;
  const provider = agent.get('metadata.cloud_provider') as string | undefined;
  const cloudLogo = provider ? CLOUD_PROVIDERS.byId(provider).logo : null;
  const labels = (agent.labels || []) as Array<{ id: string | number; get: (f: string) => string }>;

  const asn = agent.get('asn');
  const asnText = asn && String(asn) !== '0' ? agent.asFormatted : null;
  const site = agent.siteDisplayName as string | undefined;
  const cloudRegion = provider ? (agent.cloudRegion as string) : null;
  const location = agent.locationString as string | null;

  const publicIps = [
    agent.get('metadata.public_ipv4_addresses[0].value') || agent.get('ip'),
    agent.get('metadata.public_ipv6_addresses[0].value')
  ]
    .filter(Boolean)
    .join(', ');
  const privateIps = [
    agent.get('metadata.private_ipv4_addresses[0].value') || agent.get('local_ip'),
    agent.get('metadata.private_ipv6_addresses[0].value')
  ]
    .filter(Boolean)
    .join(', ');

  const status = agent.status as { icon?: string; color?: string; text?: string } | undefined;

  return (
    <Box p={2} minWidth={240} maxWidth={320}>
      <Flex alignItems="center" mb={1}>
        {cloudLogo ? (
          <Flex mr="6px" css={{ '& svg': { height: '16px', width: 'auto' } }}>
            {cloudLogo}
          </Flex>
        ) : (
          AgentIcon && (
            <Box mr="6px">
              <AgentIcon width={16} height={16} />
            </Box>
          )
        )}
        <Text fontWeight="bold" ellipsis>
          {agent.label}
        </Text>
        {status?.icon && (
          <Icon ml="auto" pl="6px" icon={status.icon} color={status.color} title={status.text} iconSize={12} />
        )}
      </Flex>

      {labels.length > 0 && (
        <Flex flexWrap="wrap" gap="4px" mb="8px">
          {labels.map((label) => (
            <Tag key={label.id} small minimal bg={label.get('color') || '#888'} color="#ffffff">
              {label.get('name')}
            </Tag>
          ))}
        </Flex>
      )}

      <Row label="Site" value={site} />
      <Row label="ASN" value={asnText} />
      <Row
        label={provider ? 'Cloud Region' : 'Location'}
        value={cloudRegion && cloudRegion !== '---' ? cloudRegion : location}
      />
      <Row label="Public IP" value={publicIps} />
      <Row label="Private IP" value={privateIps} />

      {onOpenDetails && (
        <>
          <Box my={1}>
            <Divider />
          </Box>
          <Button
            small
            minimal
            intent="primary"
            icon="maximize"
            onClick={(e: React.MouseEvent) => {
              // Popover content is portaled but still bubbles through the React tree to the
              // legend row's click handler; stop it so opening details doesn't toggle the series.
              e.stopPropagation();
              onOpenDetails();
            }}
          >
            Open details
          </Button>
        </>
      )}

      {!onOpenDetails && fullPageHref && (
        <>
          <Box my={1}>
            <Divider />
          </Box>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only stops the click toggling the legend row (see above) */}
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <LinkButton small minimal intent="primary" icon={FiMaximize2} to={fullPageHref}>
              Open Full Page
            </LinkButton>
          </div>
        </>
      )}
    </Box>
  );
});

export default AgentPopoverCard;
